import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Modal, Field } from '../ui/Modal';
import { Icon } from '../ui/Icon';
import { Chip, statusTone } from '../ui/Chip';
import { Avatar } from '../ui/Avatar';
import { api, ApiError } from '../../lib/api';
import { assignableUsersQuery } from '../../features/tasks/tasksApi';
import { OpzioniPersone } from '../../features/tasks/OpzioniPersone';
import { toast } from '../../stores/toast.store';
import { confirm as confirmDialog } from '../../stores/confirm.store';
import { TaskStatus, TaskPriority } from '@masamune/shared';
import { fmtDate } from '../../lib/utils';

interface TaskFull {
  id: string;
  title: string;
  description: string | null;
  status: string;
  priority: string;
  assigneeId: string | null;
  assignee: { firstName: string; lastName: string; initials: string } | null;
  dueDate: string | null;
  estimateHours: string | null;
  spentHours: string;
  tag: string | null;
  blockedReason: string | null;
  projectId: string;
  project: { id: string; code: string; name: string };
  dependsOn: Array<{ blockedBy: { id: string; title: string; status: string } }>;
  blocking: Array<{ task: { id: string; title: string; status: string } }>;
}

const STATUSES = ['TODO', 'IN_PROGRESS', 'REVIEW', 'BLOCKED', 'DONE'] as const;
// Le stesse parole dell'API (STATUS_LABEL di tasks.service.ts), 28/10/2026: prima erano
// in inglese (To do, Review, Done) e la finestra non parlava come il resto del gestionale.
const STATUS_LABELS: Record<string, string> = {
  TODO: 'Da fare', IN_PROGRESS: 'In corso', REVIEW: 'In revisione', BLOCKED: 'Bloccato', DONE: 'Completato',
};

/**
 * Il dettaglio di una task aperto dal progetto (kanban, Lavori, telefono). Dal 28/10/2026
 * legge la stessa voce di cache della pagina della task, ['tasks','detail',id]: la rotta
 * è la stessa, e con una chiave a parte (['task', id]) dopo un salvataggio dalla pagina la
 * finestra mostrava il testo vecchio per 30 secondi. Il tipo qui sotto descrive i campi
 * che servono alla finestra (ore, dipendenze, motivo del blocco), che la pagina non usa.
 * Niente Riscrivi qui (fase-1-progetto.md, decisione 13).
 */
export function TaskDetailModal({ onClose, taskId }: { onClose: () => void; taskId: string }) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);

  const { data: task } = useQuery({
    queryKey: ['tasks', 'detail', taskId],
    queryFn: () => api.get<TaskFull>(`/tasks/${taskId}`),
  });

  const [form, setForm] = useState<Partial<TaskFull>>({});
  useEffect(() => { if (task) setForm(task); }, [task]);

  // Chi si può assegnare: la stessa lista delle Task (senza gli agenti, con i professionisti a parte).
  const { data: users } = useQuery({ ...assignableUsersQuery, enabled: editing });

  const update = useMutation({
    mutationFn: (data: any) => api.patch(`/tasks/${taskId}`, data),
    onSuccess: () => {
      // ['tasks'] comprende anche questa finestra (['tasks','detail',id]): si rilegge da sola,
      // e con lei la pagina della task, la lista e i badge.
      qc.invalidateQueries({ queryKey: ['tasks'] });
      qc.invalidateQueries({ queryKey: ['project'] });
      toast.ok('Task aggiornato', task?.title);
      setEditing(false);
    },
    onError: (err) => toast.alert('Errore aggiornamento task', err instanceof ApiError ? err.message : 'Riprova'),
  });

  const del = useMutation({
    mutationFn: () => api.delete(`/tasks/${taskId}`),
    onSuccess: () => {
      // Come useTaskActions: tutto tranne i dettagli, che rileggerebbero la task appena tolta (404).
      qc.invalidateQueries({ queryKey: ['tasks'], predicate: (q) => q.queryKey[1] !== 'detail' });
      qc.invalidateQueries({ queryKey: ['project'] });
      toast.ok('Task eliminato', task?.title);
      onClose();
    },
    onError: (err) => toast.alert('Errore eliminazione task', err instanceof ApiError ? err.message : 'Riprova'),
  });

  const setStatus = (status: string) => update.mutate({ status });

  if (!task) {
    return (
      <Modal title="Caricamento…" onClose={onClose}>
        <div className="text-center py-8 mute">Caricamento…</div>
      </Modal>
    );
  }

  return (
    <Modal
      title={editing ? 'Modifica task' : 'Dettaglio task'}
      kicker={`${task.project.code} · ${task.project.name}`}
      onClose={onClose}
      size="lg"
      footer={
        editing ? (
          <>
            <button className="btn btn-ghost" onClick={() => { setEditing(false); setForm(task); }}>Annulla</button>
            <div className="spacer" />
            <button className="btn btn-primary" onClick={() => update.mutate({
              title: form.title,
              description: form.description,
              status: form.status,
              priority: form.priority,
              assigneeId: form.assigneeId || null,
              dueDate: form.dueDate || null,
              estimateHours: form.estimateHours ? Number(form.estimateHours) : null,
              spentHours: form.spentHours ? Number(form.spentHours) : 0,
              tag: form.tag || null,
              blockedReason: form.blockedReason || null,
            })} disabled={update.isPending}>
              Salva <Icon name="check" size={12} />
            </button>
          </>
        ) : (
          <>
            <button className="btn btn-ghost" disabled={del.isPending} onClick={async () => { if (await confirmDialog({ title: 'Eliminare questo task?', danger: true, confirmLabel: 'Elimina' })) del.mutate(); }}><Icon name="x" size={12} /> Elimina</button>
            <div className="spacer" />
            <button className="btn" onClick={() => setEditing(true)}>Modifica</button>
            <button className="btn btn-primary" onClick={onClose}>Chiudi</button>
          </>
        )
      }
    >
      {!editing ? (
        <div className="space-y-4">
          {/* Stato switcher */}
          <div>
            <div className="t-micro mb-2">Cambia stato</div>
            <div className="seg-control">
              {STATUSES.map((s) => (
                <button key={s} aria-pressed={task.status === s} onClick={() => setStatus(s)}>
                  {STATUS_LABELS[s]}
                </button>
              ))}
            </div>
          </div>

          {task.status === 'BLOCKED' && task.blockedReason && (
            <div className="p-3 rounded bg-alert-bg text-alert text-[13px] flex gap-2 items-start">
              <Icon name="alert" size={12} className="mt-0.5" />
              <div>
                <div className="font-semibold mb-0.5">Bloccato:</div>
                {task.blockedReason}
              </div>
            </div>
          )}

          <div>
            <div className="t-h2 leading-tight mb-2">{task.title}</div>
            <div className="flex flex-wrap gap-2">
              <Chip tone={task.priority === 'ALTA' ? 'alert' : task.priority === 'MEDIA' ? 'warn' : 'ghost'}>
                Priorità {task.priority.toLowerCase()}
              </Chip>
              {task.tag && <Chip>{task.tag}</Chip>}
              {task.assignee && (
                <Chip tone="ghost">
                  <Avatar initials={task.assignee.initials} size={14} /> {task.assignee.firstName[0]}. {task.assignee.lastName}
                </Chip>
              )}
              {task.dueDate && <Chip tone="ghost"><Icon name="calendar" size={10} /> {fmtDate(task.dueDate)}</Chip>}
            </div>
          </div>

          {task.description && (
            <div>
              <div className="t-micro mb-2">Descrizione</div>
              <div className=" leading-relaxed whitespace-pre-wrap">{task.description}</div>
            </div>
          )}

          {task.estimateHours && (
            <div>
              <div className="t-micro mb-2">Ore</div>
              <div className="flex items-center gap-3 text-[13px]">
                <span className="mono">{Number(task.spentHours)}h / {Number(task.estimateHours)}h</span>
                <div className="barbar flex-1">
                  <span style={{
                    width: `${Math.min(100, (Number(task.spentHours) / Number(task.estimateHours)) * 100)}%`,
                    background: Number(task.spentHours) > Number(task.estimateHours) ? 'var(--alert)' : 'var(--accent)',
                  }} />
                </div>
              </div>
            </div>
          )}

          {(task.dependsOn.length > 0 || task.blocking.length > 0) && (
            <div>
              <div className="t-micro mb-2">Dipendenze</div>
              {task.dependsOn.map((d) => (
                <div key={d.blockedBy.id} className="text-[13px] py-1 flex items-center gap-2">
                  <Icon name="arrowRight" size={10} className="mute" />
                  Bloccato da: <span className="font-medium">{d.blockedBy.title}</span>
                  <Chip tone={statusTone(d.blockedBy.status)}>{STATUS_LABELS[d.blockedBy.status]}</Chip>
                </div>
              ))}
              {task.blocking.map((b) => (
                <div key={b.task.id} className="text-[13px] py-1 flex items-center gap-2">
                  <Icon name="arrowLeft" size={10} className="mute" />
                  Blocca: <span className="font-medium">{b.task.title}</span>
                  <Chip tone={statusTone(b.task.status)}>{STATUS_LABELS[b.task.status]}</Chip>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="form-grid">
          <Field label="Titolo" required span={12}>
            <input className="inp" value={form.title ?? ''} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          </Field>
          <Field label="Descrizione" span={12}>
            <textarea className="inp" rows={4} value={form.description ?? ''} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </Field>
          <Field label="Stato" span={6}>
            <select className="inp" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
              {STATUSES.map((s) => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
            </select>
          </Field>
          <Field label="Priorità" span={6}>
            <div className="seg-control">
              {[TaskPriority.BASSA, TaskPriority.MEDIA, TaskPriority.ALTA].map((pr) => (
                <button key={pr} aria-pressed={form.priority === pr} onClick={() => setForm({ ...form, priority: pr })}>{pr}</button>
              ))}
            </div>
          </Field>
          <Field label="Assegnatario" span={6}>
            <select className="inp" value={form.assigneeId ?? ''} onChange={(e) => setForm({ ...form, assigneeId: e.target.value })}>
              <OpzioniPersone users={users} />
            </select>
          </Field>
          <Field label="Scadenza" span={6}>
            <input className="inp mono" type="date" value={form.dueDate ? form.dueDate.slice(0, 10) : ''} onChange={(e) => setForm({ ...form, dueDate: e.target.value })} />
          </Field>
          <Field label="Stima (ore)" span={3}>
            <input className="inp mono text-right" type="number" step="0.5" value={form.estimateHours ?? ''} onChange={(e) => setForm({ ...form, estimateHours: e.target.value as any })} />
          </Field>
          <Field label="Ore spese" span={3}>
            <input className="inp mono text-right" type="number" step="0.5" value={form.spentHours ?? ''} onChange={(e) => setForm({ ...form, spentHours: e.target.value as any })} />
          </Field>
          <Field label="Tag" span={6}>
            <input className="inp" placeholder="frontend, backend..." value={form.tag ?? ''} onChange={(e) => setForm({ ...form, tag: e.target.value })} />
          </Field>
          {form.status === 'BLOCKED' && (
            <Field label="Motivo del blocco" span={12}>
              <textarea className="inp" rows={2} value={form.blockedReason ?? ''} onChange={(e) => setForm({ ...form, blockedReason: e.target.value })} />
            </Field>
          )}
        </div>
      )}
    </Modal>
  );
}
